import classes from './styles.module.scss';

import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IsNullOrEmpty, IsOnlyLetters, IsValidEmail, IsValidTckn } from '../../core/utility';
import { post, postWithFile } from '../../core/client';
import ReCAPTCHA from 'react-google-recaptcha';
import { ResponseWithSuccess } from '../../core/client/Models';
import { RELATIVE_TYPES } from '../../core/consts';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import InfoIcon from '@mui/icons-material/Info';
import { CustomButton } from '../../components/buttons';
import Input from '../../components/inputs/text-field-input';
import DatePicker from '../../components/inputs/date-picker';
import UploadInput from '../../components/inputs/upload-input';
import PhoneInput from '../../components/inputs/phone';
import SmsCodeModal from '../shared/SmsCodeModal';
import JoinFormV2ContextProvider, { JoinFormV2Context, LegalContractConsentType } from '../shared/JoinFormV2Context';
import reactStringReplace from 'react-string-replace';
import DetailedInfo from '../shared/DetailedInfo';
import Typography from '@mui/material/Typography';
import WarningModal from '../../components/warning-modal';

interface StateModel {
    userId: number;
    relativeFullName: string;
    orgMemberFullName: string;
    orgMemberTcIdentityNo: string;
    relationTypeId: number;
    relationTypeName: string;
    tcIdentityNo: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    birthDate: string;
    hasRelativeFile: boolean;
    file: File | null;
    fileName: string;
    reCaptchaToken: string;
    //ileti izni
    commPerm: boolean;
    //kişisel veri paylaşım izni
    // dataSharePerm1: boolean;
    // dataSharePerm2: boolean;
    approvedConsents: Array<string>;
}

interface InputErrorModel {
    name: string;
    errorText: string;
}

interface FamilyMemberInfo {
    userId: number;
    orgMemberTcIdentityNo: string;
    orgMemberFullName: string;
    orgMemberCompanyId: number;
    firstName: string;
    lastName: string;
    tckn: string;
    relationTypeId: number;
    birthDate: string;
    phone: string | null;
    createDate: string;
}

interface CheckFamilyMemberFileResponse {
    hasRelativeFile: boolean;
}

interface CompleteFamilyMembershipResponse extends ResponseWithSuccess {
    isSendToSystem: boolean;
}

type KeyValue = {
    key: string;
    value: string;
};

const initialState: StateModel = {
    userId: 0,
    orgMemberFullName: '',
    relativeFullName: '',
    relationTypeId: 0,
    relationTypeName: '',
    tcIdentityNo: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    birthDate: '',
    commPerm: false,
    file: null,
    hasRelativeFile: false,
    orgMemberTcIdentityNo: '',
    fileName: '',
    reCaptchaToken: '',
    approvedConsents: [],
};

const relationTypes = [
    { id: 1, name: 'ANNE' },
    { id: 2, name: 'BABA' },
    { id: 3, name: 'EŞ' },
    { id: 4, name: 'ÇOCUK' },
    { id: 5, name: 'KARDEŞ' },
];

const FamilyMemberJoinFormComponent = () => {
    const { legalTextLoading, legalTexts, loadLegalTexts, openLegalTextModal } = useContext(JoinFormV2Context);

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const params: KeyValue[] = [];

    searchParams.forEach((value, key) => {
        params.push({ key, value });
    });

    //states
    const [state, setState] = useState<StateModel>(initialState);
    const [loading, setLoading] = useState(false);
    const [loadingSendForm, setLoadingSendForm] = useState(false);
    const [loadingCheckFile, setLoadingCheckFile] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [inputErrors, setInputErrors] = useState<Array<InputErrorModel>>([]);
    const [isKvkkError, setIsKvkkError] = useState(false);
    const [showSmsCodeModal, setShowSmsCodeModal] = useState(false);
    const [setshowFileSelect, setSetshowFileSelect] = useState(false);

    const [openPopup, setOpenPopup] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');

    const handleWarnigOpen = (msg: string) => {
        setMessage(msg);
        setOpenPopup(true);
    };

    const handleWarningClose = () => setOpenPopup(false);

    const recaptchaRef = useRef<any>(null);

    useEffect(() => {
        if (params && params.length > 0) {
            setLoading(prev => true);
            initialLoad();
            setLoading(prev => false);
        }
    }, []);

    //methods
    const initialLoad = async () => {
        var data = await loadFamilyMemberInfo(params);
        await checkMemberAndMemberRequestExistance(data);
        if (data) {
            setState(prev => ({
                ...data,
            }));
        }
        loadLegalTexts([
            LegalContractConsentType.VERI_IZNI,
            LegalContractConsentType.YKB_VERI_AKTARIM_IZNI,
            LegalContractConsentType.ETK_IZNI,
            LegalContractConsentType.YURT_DISI_VERI_IZNI,
            LegalContractConsentType.UYELIK_SOZLESMESI,
            LegalContractConsentType.YKB_AYDINLATMA
        ]);
    }

    const loadFamilyMemberInfo = async (params: KeyValue[]) => {
        const { data, fail, errorMessage } = await post<{ familyMemberInfo: FamilyMemberInfo }>(`api/membership/get-family-member-info`, { params });
        let newState: StateModel = initialState;
        if (data) {
            const familyMemberInfo = data.familyMemberInfo;
            newState = {
                ...initialState,
                userId: familyMemberInfo.userId,
                orgMemberFullName: familyMemberInfo.orgMemberFullName,
                orgMemberTcIdentityNo: familyMemberInfo.orgMemberTcIdentityNo,
                relativeFullName: `${familyMemberInfo.firstName} ${familyMemberInfo.lastName}`,
                firstName: familyMemberInfo.firstName,
                lastName: familyMemberInfo.lastName,
                tcIdentityNo: familyMemberInfo.tckn,
                birthDate: familyMemberInfo.birthDate,
                relationTypeId: familyMemberInfo.relationTypeId,
                relationTypeName: relationTypes.find(x => x.id === familyMemberInfo.relationTypeId)?.name ?? '',
                phone: familyMemberInfo.phone
                    ? familyMemberInfo.phone.startsWith('90')
                        ? familyMemberInfo.phone.substring(2)
                        : familyMemberInfo.phone
                    : '',
            };
        }
        else {
            window.alert('Aile üye bilgisi bulunamadı');
        }
        if (fail) {
            setErrorText(errorMessage);
        }
        return newState;
    };

    const checkMemberAndMemberRequestExistance = async (stateData: StateModel) => {
        const request = {
            tcIdentityNo: stateData.tcIdentityNo,
            mobilePhone: stateData.phone,
        };
        const { data, fail, errorMessage } = await post<{ isExist: boolean }>(`api/membership/membership-existance`, request);
        if (fail) {
            if (stateData.relationTypeId === 0) {
                navigate('/join-form/congrats');
            }
            navigateRelativeCongrastsPage(stateData);
        }
    };

    const navigateRelativeCongrastsPage = (relativeInfo: StateModel) => {
        const relationTypeId = relativeInfo.relationTypeId;
        if (relationTypeId === RELATIVE_TYPES.Anne || relationTypeId === RELATIVE_TYPES.Baba) {
            navigate('/join-form/congrats-parents', { state: { parentName: relativeInfo.relativeFullName } });
        }
        if (relationTypeId === RELATIVE_TYPES.Es) {
            navigate('/join-form/congrats-spouse', { state: { parentName: relativeInfo.relativeFullName } });
        }
        if (relationTypeId === RELATIVE_TYPES.Cocuk || relationTypeId === RELATIVE_TYPES.Kardes) {
            navigate('/join-form/congrats-child', { state: { parentName: relativeInfo.relativeFullName } });
        }
    };

    const isConsentApproved = (type: string) => {
        return state.approvedConsents.some(consent => consent === type);
    };

    const handleChange = (e: any) => {
        setState(prev => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleOpenSelectFile = async () => {
        if (IsNullOrEmpty(state.tcIdentityNo)) {
            window.alert('Dosya yüklemeden önce TC Kimlik No giriniz');
            return;
        }

        setLoadingCheckFile(true);
        const request = { tcIdentityNo: state.tcIdentityNo };
        const { data, fail, errorMessage } = await post<CheckFamilyMemberFileResponse>('api/membership/check-family-member-file', request);
        setLoadingCheckFile(false);
        if (data && data.hasRelativeFile) {
            setState(prev => ({ ...prev, hasRelativeFile: true }));
        }

        setSetshowFileSelect(true);
    };

    const handleFileInput = (files: FileList | []) => {
        setState(prevState => ({
            ...prevState,
            file: files.length > 0 ? files[0] : null,
            fileName: files.length > 0 ? files[0].name : '',
        }));
    };

    const onChangeCaptcha = async (e: any) => {
        setState(prev => ({
            ...prev,
            reCaptchaToken: e,
        }));
    };

    const toggleSmsModal = (show: boolean) => {
        setShowSmsCodeModal(show);
    };
    const handleMembershipAggreement = () => {
        openLegalTextModal(LegalContractConsentType.UYELIK_SOZLESMESI);
    };
    const handleYkbAggreement = () => {
        openLegalTextModal(LegalContractConsentType.YKB_AYDINLATMA);
    };

    const handleConsentCheckbox = (e: any) => {
        const consentType = e.target.name;
        let consents: Array<string> = [...state.approvedConsents];
        consents = consents.filter(x => x !== consentType);

        if (e.target.checked) {
            consents = [...consents, consentType];

            if (
                consentType === LegalContractConsentType.VERI_IZNI ||
                consentType === LegalContractConsentType.YURT_DISI_VERI_IZNI ||
                consentType === LegalContractConsentType.VAKIF_DERNEK_IZNI
            ) {
                openLegalTextModal(consentType as LegalContractConsentType);
            }
        }
        setState(prev => ({
            ...prev,
            approvedConsents: consents,
        }));
    };

    const isRequiredConsentsChecked = (model: StateModel) => {
        const requiredConsents = legalTexts?.consents.filter(
            x => x.required && x.type !== LegalContractConsentType.UYELIK_SOZLESMESI && x.type !== LegalContractConsentType.YKB_AYDINLATMA,
        );
        if (requiredConsents) {
            for (let item of requiredConsents) {
                if (!isConsentApproved(item.type)) {
                    return;
                }
            }
        }
        return true;
    };

    const setError = (name: keyof StateModel, errorText = 'Zorunlu alan') => {
        const error: InputErrorModel = { name, errorText };
        setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
    };

    const validate = (model: StateModel): boolean => {
        setIsKvkkError(false);
        setInputErrors([]);

        let isValid = true;

        debugger;

        if (!IsValidTckn(model.tcIdentityNo)) {
            setError('tcIdentityNo', 'Geçersi TC kimlik no');
            isValid = false;
        }
        if (IsNullOrEmpty(model.firstName) || !IsOnlyLetters(model.firstName)) {
            setError('firstName', 'Geçersiz ad');
            isValid = false;
        }
        if (IsNullOrEmpty(model.lastName) || !IsOnlyLetters(model.lastName)) {
            setError('lastName', 'Geçersiz soyad');
            isValid = false;
        }
        if (IsNullOrEmpty(model.birthDate)) {
            setError('birthDate', 'Geçersiz doğum tarihi');
            isValid = false;
        }
        if (IsNullOrEmpty(model.phone) || model.phone[0] !== '5' || model.phone.length !== 10) {
            setError('phone', 'Geçersiz telefon');
            isValid = false;
        }
        if (!IsValidEmail(model.email)) {
            setError('email', 'Geçersiz e-posta');
            isValid = false;
        }
        if (!isRequiredConsentsChecked(model)) {
            setIsKvkkError(true);
            isValid = false;
        }

        return isValid;
    };

    const handleSend = async (model: StateModel) => {
        setErrorText('');

        if (!validate(model)) return;
        setLoadingSendForm(true);
        const request = {
            relationTypeId: model.relationTypeId,
            orgMemberTcIdentityNo: model.orgMemberTcIdentityNo,
            tcIdentityNo: model.tcIdentityNo,
            firstName: model.firstName,
            lastName: model.lastName,
            birthDate: model.birthDate,
            phone: '90' + model.phone,
            email: model.email,
            isFileUploaded: model.file !== null,
            reCaptchaToken: model.reCaptchaToken,
        };
        const { data, fail, errorMessage } = await post<ResponseWithSuccess>('api/membership/validate-family-form', request);
        setLoadingSendForm(false);
        if (data && data.success) {
            toggleSmsModal(true);
        }

        if (fail) {
            setErrorText(errorMessage);
        }

        (window as any).grecaptcha.reset();
    };

    const handleSmsValidated = async (code: string, model: StateModel) => {
        setErrorText('');

        setLoadingSendForm(true);
        const request = {
            smsCode: code,
            userId: model.userId,
            orgMemberTcIdentityNo: model.orgMemberTcIdentityNo,
            relationTypeId: model.relationTypeId,
            tcIdentityNo: model.tcIdentityNo,
            firstName: model.firstName,
            lastName: model.lastName,
            birthDate: model.birthDate,
            phone: '90' + model.phone,
            email: model.email,
            isApprovedVeriIzni: isConsentApproved(LegalContractConsentType.VERI_IZNI),
            isApprovedYkbAktarim: isConsentApproved(LegalContractConsentType.YKB_VERI_AKTARIM_IZNI),
            commPermStatus: isConsentApproved(LegalContractConsentType.ETK_IZNI),
            hasRelativeFile: model.hasRelativeFile,
            file: model.file,
            fileName: model.fileName,
        };
        const { data, fail, errorMessage } = await postWithFile<CompleteFamilyMembershipResponse>('api/membership/complete-family-form', request);
        setLoadingSendForm(false);
        if (data && data.success) {
            if (data.isSendToSystem) {
                navigate('/join-form/congrats');
            } else {
                if (model.relationTypeId === RELATIVE_TYPES.Anne || model.relationTypeId === RELATIVE_TYPES.Baba) {
                    navigate('/join-form/congrats-parents', { state: { parentName: state.relativeFullName } });
                }
                if (model.relationTypeId === RELATIVE_TYPES.Es) {
                    navigate('/join-form/congrats-spouse', { state: { parentName: state.relativeFullName } });
                }
                if (model.relationTypeId === RELATIVE_TYPES.Cocuk) {
                    navigate('/join-form/congrats-child', { state: { parentName: state.relativeFullName } });
                }
                if (model.relationTypeId === RELATIVE_TYPES.Kardes) {
                    navigate('/join-form/congrats-child', { state: { parentName: state.relativeFullName } });
                }
            }
        }
        else {
            handleWarnigOpen('İşleminiz tamamlanamadı. Bilgi almak için 444 0 447 numaralı KoçAilem Çağrı Merkezi ile iletişime geçebilirsiniz. ');      
            return;
        }

        if (fail) {
            setErrorText(errorMessage);
        }
    };

    return (
        <div className={classes.familyMemberJoinForm}>
            <div className={classes.overlay}>
                <div className={classes.container}>
                    <div className={classes.form}>
                        <div className={classes.text}>
                            <p>
                                Sevgili {state.relativeFullName}, {state.orgMemberFullName} Seni KoçAilemli olmaya davet etti.
                            </p>
                            <p>Aşağıdaki formu doldurarak üyeliğini hemen başlatabilirsin.</p>
                            <p>KoçAilem'e şimdiden hoş geldin!</p>
                        </div>
                        <h1>AİLE YAKINI DAVET FORMU</h1>
                        <Grid container spacing={2}>
                            <Grid item sm={6} xs={12}>
                                <Input
                                    id="relationTypeName"
                                    name="relationTypeName"
                                    caption="Yakınlık Tipi"
                                    disabled
                                    value={state.relationTypeName}
                                />
                                <input id="relationTypeId" name="relationTypeId" value={state.relationTypeId} hidden />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <Input
                                    id="tcIdentityNo"
                                    name="tcIdentityNo"
                                    caption="TC Kimlik No"
                                    patternFormat={{
                                        format: '###########',
                                    }}
                                    value={state.tcIdentityNo}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'tcIdentityNo')?.errorText}
                                />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <Input
                                    id="firstName"
                                    name="firstName"
                                    caption="Ad"
                                    toUpperCase
                                    value={state.firstName}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'firstName')?.errorText}
                                />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <Input
                                    id="lastName"
                                    name="lastName"
                                    caption="Soyad"
                                    toUpperCase
                                    value={state.lastName}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'lastName')?.errorText}
                                />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <DatePicker
                                    id="birthDate"
                                    name="birthDate"
                                    caption="Doğum Tarihi"
                                    value={state.birthDate}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'birthDate')?.errorText}
                                />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <PhoneInput
                                    id="phone"
                                    name="phone"
                                    caption="Telefon"
                                    placeholder="(5##) ### ## ##"
                                    format="(###) ### ## ##"
                                    value={state.phone}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'phone')?.errorText}
                                />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <Input
                                    id="email"
                                    name="email"
                                    caption="Kişisel E-Posta"
                                    value={state.email}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'email')?.errorText}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {!setshowFileSelect ? (
                                    <CustomButton
                                        text="Vukuatlı Aile Nüfus Kayıt Örneği Yükle"
                                        className="btn-secondary"
                                        fullWidth
                                        loading={loadingCheckFile}
                                        onClick={handleOpenSelectFile}
                                    />
                                ) : (
                                    <>
                                        <p>
                                            {' '}
                                            E-devlet üzerinden alınacak Nüfus Kayıt Örneği Belgesi menüsünden Nüfus Kayıt Örneği Tipi'nin{' '}
                                            <strong>Aile</strong> seçilme gerekmektedir
                                        </p>
                                        <UploadInput
                                            text="Dosya Seç"
                                            disabled={state.hasRelativeFile}
                                            options={{
                                                accept: 'application/pdf',
                                                onChange: handleFileInput,
                                                multiple: false,
                                            }}
                                        />
                                        {state.hasRelativeFile && <p>Aile yakınına ait doküman mevcuttur.</p>}
                                        <Box display="flex" marginTop="8px">
                                            <InfoIcon fontSize="small" color="action" />
                                            <Typography variant="body2" color="textSecondary" ml={0.5}>
                                                Bu alana yükleyeceğiniz dosya yalnızca PDF uzantılı olabilir.
                                            </Typography>
                                        </Box>
                                        <DetailedInfo />
                                    </>
                                )}
                            </Grid>
                        </Grid>
                        <div className="py-3">
                            {legalTextLoading ? (
                                <p>Yükleniyor...</p>
                            ) : (
                                legalTexts?.consents
                                    .filter(
                                        x =>
                                            x.type !== LegalContractConsentType.UYELIK_SOZLESMESI &&
                                            x.type !== LegalContractConsentType.YKB_AYDINLATMA,
                                    )
                                    .map((item, index) => (
                                        <div className="py-1" key={index}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name={item.type}
                                                        color="primary"
                                                        checked={isConsentApproved(item.type)}
                                                        onChange={handleConsentCheckbox}
                                                    />
                                                }
                                                label={
                                                    <div
                                                        className={
                                                            item.required && isKvkkError && !isConsentApproved(item.type)
                                                                ? `${classes.errorCheckbox}`
                                                                : ''
                                                        }
                                                        dangerouslySetInnerHTML={{ __html: item.text }}></div>
                                                }
                                            />
                                        </div>
                                    ))
                            )}
                        </div>

                        <Box marginBottom={2}>{errorText && <Alert severity="error">{errorText}</Alert>}</Box>
                        <Box display="flex" justifyContent="center" marginBottom={2}>
                            <ReCAPTCHA ref={recaptchaRef} sitekey="6LdqH0QfAAAAAAQR10C9Poo7LItjCQ0Fbe2l9Fnn" onChange={onChangeCaptcha} />
                        </Box>
                        <Box marginBottom={2} textAlign="center">
                            {reactStringReplace(
                                legalTexts?.consents.find(x => x.type === LegalContractConsentType.YKB_AYDINLATMA)?.text ?? '',
                                /<strong>(.*?)<\/strong>/,
                                (match, i) => (
                                    <button style={{ color: '#177897', cursor: 'pointer', border: 'none' }} onClick={handleYkbAggreement}>
                                        <strong>{match}</strong>
                                    </button>
                                ),
                            )}
                        </Box>
                        <Box marginBottom={2} textAlign="center">
                            'KOÇAİLEM ÜYELİĞİMİ TAMAMLA' butonuna basılması ile{' '}
                            <button style={{ color: '#177897', cursor: 'pointer', border: 'none' }} onClick={handleMembershipAggreement}>
                                <strong>Üyelik Sözleşmesi</strong>
                            </button>
                            'ni okuduğunuzu ve kabul ettiğinizi onaylıyorsunuz.
                        </Box>
                        <Box display="flex" justifyContent="center" marginBottom={2}>
                            <CustomButton
                                text="KOÇAİLEM ÜYELİĞİMİ TAMAMLA"
                                className="btn-primary"
                                disabled={loading || loadingSendForm}
                                loading={loadingSendForm}
                                onClick={() => handleSend(state)}
                            />
                        </Box>
                    </div>
                </div>
            </div>
            <SmsCodeModal
                show={showSmsCodeModal}
                phone={state.phone}
                onSend={(val: string) => handleSmsValidated(val, state)}
                onClose={() => toggleSmsModal(false)}
            />
             <WarningModal
                open={openPopup}
                message={message}
                handleClose={() => handleWarningClose()}
            />
        </div>
    );
};

const FamilyMemberJoinForm = () => (
    <JoinFormV2ContextProvider>
        <FamilyMemberJoinFormComponent />
    </JoinFormV2ContextProvider>
);

export default FamilyMemberJoinForm;
